// client/src/screens/admin/AdminProductFormScreen.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  createAdminProduct,
  getAdminProductDetails,
  resetAdminProductCreate,
  resetAdminProductDetails,
  resetAdminProductUpdate,
  updateAdminProduct,
} from "../../actions/adminProductActions";

import {
  HABERDASHERY_SHOP_KEY,
  READY_MADE_SHOP_KEY,
  SHOPS,
  TEXTILE_SHOP_KEY,
  isValidShopKey,
} from "../../constants/shopMeta";
import { getSafeUserMessage } from "../../utils/errorMessages";

import HaberdasheryProductForm from "./products/HaberdasheryProductForm";
import ReadyMadeProductForm from "./products/ReadyMadeProductForm";
import TextilesProductForm from "./products/TextilesProductForm";

import "./products/ProductForm.css";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const DEFAULT_SHOP_KEY = TEXTILE_SHOP_KEY;

const LIFECYCLE_FIELD_NAMES = new Set([
  "status",
  "isPublished",
  "isDeleted",
  "publishedAt",
  "publishedBy",
  "deletedAt",
  "deletedBy",
  "restoredAt",
  "restoredBy",
]);

const VALID_RETURN_STATUSES = new Set(["all", "draft", "published", "deleted"]);

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeShopKey(value = "") {
  const next = safeText(value);
  return isValidShopKey(next) ? next : "";
}

function normalizeReturnStatus(value = "") {
  const next = normalizeLower(value);
  return VALID_RETURN_STATUSES.has(next) ? next : "";
}

function getShopMeta(shopKey = "") {
  const normalized = normalizeShopKey(shopKey);
  return SHOPS.find((shop) => shop.shopKey === normalized) || null;
}

function getShopLabel(shopKey = "") {
  return getShopMeta(shopKey)?.name || shopKey || "Unknown shop";
}

function getShopDescription(shopKey = "") {
  return getShopMeta(shopKey)?.description || "";
}

function getFallbackShopKey(...values) {
  for (const value of values) {
    const normalized = normalizeShopKey(value);
    if (normalized) return normalized;
  }

  return DEFAULT_SHOP_KEY;
}

function getProductId(product = null) {
  return safeText(product?._id || product?.id);
}

function normalizeErrorMessage(errorLike) {
  if (!errorLike) return "";
  if (typeof errorLike === "string") {
    return getSafeUserMessage(
      errorLike,
      "We could not save this product right now. Please try again later.",
    );
  }

  return getSafeUserMessage(
    errorLike?.message,
    "We could not save this product right now. Please try again later.",
  );
}

function isConflictError(errorMeta) {
  return Number(errorMeta?.status) === 409;
}

function getConflictMessage(errorMeta) {
  const code = safeText(errorMeta?.code);

  switch (code) {
    case "VERSION_CONFLICT":
      return "This product was modified by another request. Reload the product before saving again.";
    case "PRODUCT_SKU_EXISTS":
      return "A product or variant with this SKU already exists.";
    case "PRODUCT_SLUG_EXISTS":
      return "A product with this slug already exists in this shop.";
    case "PRODUCT_EXTERNAL_ID_EXISTS":
      return "A product with this external ID already exists in this shop.";
    default:
      return normalizeErrorMessage(errorMeta) || "Conflict detected.";
  }
}

function getSubmitError({
  createError,
  createErrorMeta,
  updateError,
  updateErrorMeta,
}) {
  if (isConflictError(updateErrorMeta)) {
    return getConflictMessage(updateErrorMeta);
  }

  if (isConflictError(createErrorMeta)) {
    return getConflictMessage(createErrorMeta);
  }

  return (
    normalizeErrorMessage(updateError) || normalizeErrorMessage(createError)
  );
}

function stripLifecycleFields(payload = {}) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([key]) => !LIFECYCLE_FIELD_NAMES.has(key)),
  );
}

function buildReturnToListUrl({
  createdProduct,
  updatedProduct,
  product,
  effectiveShopKey,
  requestedShopKey,
  selectedShopKey,
  returnStatus,
}) {
  const shopKey = getFallbackShopKey(
    createdProduct?.shopKey,
    updatedProduct?.shopKey,
    product?.shopKey,
    effectiveShopKey,
    requestedShopKey,
    selectedShopKey,
    DEFAULT_SHOP_KEY,
  );

  const params = new URLSearchParams();
  params.set("shopKey", shopKey);

  if (returnStatus) {
    params.set("status", returnStatus);
  }

  return `/admin/products?${params.toString()}`;
}

/* -------------------------------------------------------------------------- */
/*                                   Screen                                   */
/* -------------------------------------------------------------------------- */

const AdminProductFormScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const isEditing = Boolean(id);

  const requestedShopKey = useMemo(
    () => normalizeShopKey(searchParams.get("shopKey")),
    [searchParams],
  );

  const returnStatus = useMemo(
    () => normalizeReturnStatus(searchParams.get("status")),
    [searchParams],
  );

  const adminProductDetails = useSelector(
    (state) => state.adminProductDetails || {},
  );
  const adminProductCreate = useSelector(
    (state) => state.adminProductCreate || {},
  );
  const adminProductUpdate = useSelector(
    (state) => state.adminProductUpdate || {},
  );

  const {
    loading: loadingDetails = false,
    error: rawErrorDetails = "",
    errorMeta: errorMetaDetails = null,
    product = null,
  } = adminProductDetails;

  const {
    loading: loadingCreate = false,
    error: rawErrorCreate = "",
    errorMeta: errorMetaCreate = null,
    success: successCreate = false,
    product: createdProduct = null,
  } = adminProductCreate;

  const {
    loading: loadingUpdate = false,
    error: rawErrorUpdate = "",
    errorMeta: errorMetaUpdate = null,
    success: successUpdate = false,
    product: updatedProduct = null,
  } = adminProductUpdate;

  const [selectedShopKey, setSelectedShopKey] = useState(
    requestedShopKey || DEFAULT_SHOP_KEY,
  );

  const previousSuccessRef = useRef({
    create: false,
    update: false,
  });

  const lastToastRef = useRef("");

  const isSaving = loadingCreate || loadingUpdate;

  const errorDetails = useMemo(
    () =>
      isConflictError(errorMetaDetails)
        ? getConflictMessage(errorMetaDetails)
        : normalizeErrorMessage(rawErrorDetails),
    [errorMetaDetails, rawErrorDetails],
  );

  const submitError = useMemo(
    () =>
      getSubmitError({
        createError: rawErrorCreate,
        createErrorMeta: errorMetaCreate,
        updateError: rawErrorUpdate,
        updateErrorMeta: errorMetaUpdate,
      }),
    [errorMetaCreate, errorMetaUpdate, rawErrorCreate, rawErrorUpdate],
  );

  const effectiveShopKey = useMemo(
    () =>
      getFallbackShopKey(
        product?.shopKey,
        selectedShopKey,
        requestedShopKey,
        DEFAULT_SHOP_KEY,
      ),
    [product?.shopKey, requestedShopKey, selectedShopKey],
  );

  const activeShop = useMemo(
    () => getShopMeta(effectiveShopKey),
    [effectiveShopKey],
  );

  const activeShopLabel = useMemo(
    () => getShopLabel(effectiveShopKey),
    [effectiveShopKey],
  );

  const activeShopDescription = useMemo(
    () => getShopDescription(effectiveShopKey),
    [effectiveShopKey],
  );

  const returnToListUrl = useMemo(
    () =>
      buildReturnToListUrl({
        createdProduct,
        updatedProduct,
        product,
        effectiveShopKey,
        requestedShopKey,
        selectedShopKey,
        returnStatus,
      }),
    [
      createdProduct,
      effectiveShopKey,
      product,
      requestedShopKey,
      returnStatus,
      selectedShopKey,
      updatedProduct,
    ],
  );

  const reloadProduct = useCallback(() => {
    if (!id) return;
    dispatch(getAdminProductDetails(id));
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(resetAdminProductCreate());
    dispatch(resetAdminProductUpdate());

    if (id) {
      dispatch(getAdminProductDetails(id));
    } else {
      dispatch(resetAdminProductDetails());
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (!product?.shopKey) {
      if (!isEditing) {
        setSelectedShopKey(requestedShopKey || DEFAULT_SHOP_KEY);
      }

      return;
    }

    const normalized = normalizeShopKey(product.shopKey);
    if (normalized) setSelectedShopKey(normalized);
  }, [isEditing, product?.shopKey, requestedShopKey]);

  useEffect(() => {
    const message = errorDetails || submitError;
    if (!message || lastToastRef.current === message) return;

    lastToastRef.current = message;
    toast.error(message);
  }, [errorDetails, submitError]);

  useEffect(() => {
    const becameCreateSuccess =
      !previousSuccessRef.current.create && successCreate;

    const becameUpdateSuccess =
      !previousSuccessRef.current.update && successUpdate;

    if (becameCreateSuccess || becameUpdateSuccess) {
      toast.success(
        becameCreateSuccess
          ? "Product created successfully."
          : "Product updated successfully.",
      );

      navigate(returnToListUrl, { replace: true });
    }

    previousSuccessRef.current = {
      create: successCreate,
      update: successUpdate,
    };
  }, [navigate, returnToListUrl, successCreate, successUpdate]);

  useEffect(() => {
    return () => {
      dispatch(resetAdminProductCreate());
      dispatch(resetAdminProductUpdate());
      dispatch(resetAdminProductDetails());
    };
  }, [dispatch]);

  const handleBackToList = useCallback(() => {
    navigate(returnToListUrl);
  }, [navigate, returnToListUrl]);

  const handleShopChange = useCallback((event) => {
    const nextShopKey = normalizeShopKey(event.target.value);
    setSelectedShopKey(nextShopKey || DEFAULT_SHOP_KEY);
  }, []);

  const handleSubmit = useCallback(
    async (payload) => {
      const resolvedShopKey = getFallbackShopKey(
        payload?.shopKey,
        effectiveShopKey,
        selectedShopKey,
        requestedShopKey,
        DEFAULT_SHOP_KEY,
      );

      const contentPayload = stripLifecycleFields(payload);

      const normalizedPayload = {
        ...contentPayload,
        shopKey: isEditing
          ? product?.shopKey || resolvedShopKey
          : resolvedShopKey,
      };

      if (isEditing) {
        return dispatch(updateAdminProduct(id, normalizedPayload));
      }

      return dispatch(createAdminProduct(normalizedPayload));
    },
    [
      dispatch,
      effectiveShopKey,
      id,
      isEditing,
      product?.shopKey,
      requestedShopKey,
      selectedShopKey,
    ],
  );

  const commonProps = useMemo(
    () => ({
      key: `${id || "new"}-${effectiveShopKey || "none"}`,
      initialData: product || null,
      onSubmit: handleSubmit,
      loading: isSaving,
      error: submitError || null,
      errorMeta: errorMetaUpdate || errorMetaCreate || null,
      saveSuccess: successCreate || successUpdate,
      shopKey: effectiveShopKey,
      isEditing,
    }),
    [
      effectiveShopKey,
      errorMetaCreate,
      errorMetaUpdate,
      handleSubmit,
      id,
      isEditing,
      isSaving,
      product,
      submitError,
      successCreate,
      successUpdate,
    ],
  );

  const conflictMeta = useMemo(() => {
    if (isConflictError(errorMetaUpdate)) return errorMetaUpdate;
    if (isConflictError(errorMetaCreate)) return errorMetaCreate;
    if (isConflictError(errorMetaDetails)) return errorMetaDetails;
    return null;
  }, [errorMetaCreate, errorMetaDetails, errorMetaUpdate]);

  const renderConflictBanner = () => {
    if (!conflictMeta) return null;

    return (
      <div className="admin-feedback admin-feedback--error admin-product-conflict">
        <div>
          <strong>Conflict detected.</strong> {getConflictMessage(conflictMeta)}
        </div>

        {id ? (
          <button
            type="button"
            className="admin-button admin-button--small admin-button--ghost"
            onClick={reloadProduct}
            disabled={loadingDetails || isSaving}
          >
            Reload product
          </button>
        ) : null}
      </div>
    );
  };

  const renderForm = () => {
    if (isEditing && loadingDetails) {
      return (
        <div className="admin-feedback admin-feedback--info">
          Loading product...
        </div>
      );
    }

    if (isEditing && errorDetails) {
      return (
        <div className="admin-feedback admin-feedback--error">
          {errorDetails}
        </div>
      );
    }

    if (isEditing && !product) {
      return (
        <div className="admin-feedback admin-feedback--info">
          Loading product...
        </div>
      );
    }

    switch (effectiveShopKey) {
      case HABERDASHERY_SHOP_KEY:
        return (
          <HaberdasheryProductForm
            {...commonProps}
            submitLabel={
              isEditing
                ? "Update Haberdashery Product"
                : "Create Haberdashery Product"
            }
          />
        );

      case READY_MADE_SHOP_KEY:
        return (
          <ReadyMadeProductForm
            {...commonProps}
            submitLabel={
              isEditing
                ? "Update Ready-Made Product"
                : "Create Ready-Made Product"
            }
          />
        );

      case TEXTILE_SHOP_KEY:
        return (
          <TextilesProductForm
            {...commonProps}
            submitLabel={
              isEditing ? "Update Textile Product" : "Create Textile Product"
            }
          />
        );

      default:
        return (
          <div className="admin-card admin-card--ghost">
            Select a shop to start creating a product.
          </div>
        );
    }
  };

  const productId = getProductId(product);

  return (
    <div className="admin-product-page">
      <header className="admin-product-header">
        <div className="admin-product-header__top">
          <div>
            <h1 className="admin-product-title">
              {isEditing ? "Edit Product" : "Create Product"}
            </h1>

            <p className="admin-product-subtitle">
              Edit product content only. Publishing, draft state, restore, and
              deletion are handled from the product list lifecycle controls.
            </p>
          </div>

          <div className="admin-product-header__actions">
            <button
              type="button"
              className="admin-button admin-button--ghost"
              onClick={handleBackToList}
              disabled={isSaving}
            >
              Back to products
            </button>

            {isEditing ? (
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={reloadProduct}
                disabled={loadingDetails || isSaving}
              >
                Reload
              </button>
            ) : null}
          </div>
        </div>

        {activeShop ? (
          <div className="admin-product-active-shop">
            <span className="admin-product-active-shop__label">
              Active shop:
            </span>

            <span className="admin-product-active-shop__value">
              {activeShopLabel}
            </span>

            {activeShopDescription ? (
              <span className="admin-product-active-shop__hint">
                {activeShopDescription}
              </span>
            ) : null}

            {isEditing && productId ? (
              <span className="admin-product-active-shop__hint">
                Product ID: {productId}
              </span>
            ) : null}
          </div>
        ) : null}
      </header>

      {renderConflictBanner()}

      {!isEditing ? (
        <div className="admin-product-shop-switcher">
          <label className="admin-field">
            <span className="admin-field__label">Shop</span>

            <select
              className="admin-select"
              value={selectedShopKey}
              onChange={handleShopChange}
              disabled={isSaving}
            >
              {SHOPS.map((shop) => (
                <option key={shop.shopKey} value={shop.shopKey}>
                  {shop.name}
                </option>
              ))}
            </select>

            {activeShopDescription ? (
              <span className="admin-field__hint">{activeShopDescription}</span>
            ) : null}
          </label>
        </div>
      ) : (
        <div className="admin-product-shop-lock">
          <div className="admin-feedback admin-feedback--info">
            Shop is locked while editing an existing product.
          </div>
        </div>
      )}

      <div className="admin-form-shell">{renderForm()}</div>
    </div>
  );
};

export default AdminProductFormScreen;
